import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  Lock,
  LogIn,
  LogOut,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getSignedUrl,
  useActiveBooking,
  useCreateProtocol,
  useProtocolsForBooking,
} from "@/hooks/useProtocol";
import { useNowTick } from "@/hooks/useNowTick";
import { openPendingDocumentTab } from "@/lib/open-document-tab";
import { formatMsg, type ProtocolCopy } from "@/lib/protocol-copy";
import { getProtocolCopy } from "@/lib/protocol-copy-all-languages";
import { type Protocol, type ProtocolKind } from "@/lib/protocol";
import {
  computeKindState,
  computeProtocolActionNeeded,
  computeProtocolPrimary,
  pickLatest,
  type KindState,
  type ProtocolAction,
} from "@/lib/protocol-action";

type Props = {
  /** Called after a draft is created (or resumed) and the flow should take over. */
  onStart: (protocolId: string) => void;
};

/**
 * Entry surface for the protocol flow. Shows:
 *   • Hero (welcome + booking + benefits)
 *   • Status of move-in and move-out protocols (not started / draft / submitted / locked)
 *   • A single primary CTA for whichever protocol is next
 */
export function ProtocolEntry({ onStart }: Props) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = getProtocolCopy(language);
  const { data: booking, isLoading: bookingLoading } = useActiveBooking();
  const {
    data: protocols = [],
    isLoading: protocolsLoading,
    isPending: protocolsPending,
  } = useProtocolsForBooking(booking?.id ?? null);
  const create = useCreateProtocol();

  const firstName =
    (user?.user_metadata?.name as string | undefined)?.split(" ")[0] ?? "";

  const moveIn = pickLatest(
    protocols,
    "move_in",
    booking?.move_in_protocol_reopened_at ?? null
  );
  const moveOut = pickLatest(
    protocols,
    "move_out",
    booking?.move_out_protocol_reopened_at ?? null
  );

  // A lead-time/window boundary can pass while this card stays mounted with
  // no new query data — the tick forces a recompute so it doesn't go stale
  // (and stays in step with the tab strip's own `useProtocolActionNeeded`,
  // which ticks the same way).
  const now = useNowTick();
  const moveInState = computeKindState(moveIn, booking, "move_in", now);
  const moveOutState = computeKindState(moveOut, booking, "move_out", now);

  // What the single primary CTA does (resume a draft, else start the next
  // kind) — shared with the dashboard tab strip via protocol-action.ts.
  const primary = computeProtocolPrimary({
    moveInState,
    moveOutState,
    moveIn,
    moveOut,
  });
  // The "action needed" nudge (COL-368): the same predicate that lights the
  // pulsing pip on the Check-in/out tab, so the tab and this card agree. Held
  // on `isPending` (not `isLoading`, which is false for a render where the
  // query is enabled but not yet fetching) until the list has loaded —
  // exactly like the tab strip's hook, or the two could briefly disagree.
  const actionNeeded: ProtocolAction | null = protocolsPending
    ? null
    : computeProtocolActionNeeded({ booking, protocols, now });

  const [viewingPdfOf, setViewingPdfOf] = useState<string | null>(null);

  /**
   * Show the protocol's PDF, rather than navigating into the flow to find a
   * download button.
   *
   * "View PDF" used to call `onStart`, which routed into the wizard; the
   * wizard resolved a submitted protocol to its Done step, and the button
   * waiting there SAVES the file instead of showing it. So a label promising
   * a view produced a screen change followed by a download — two steps, and
   * neither of them a view. Every other surface that shows this same PDF (the
   * owner feed, the owner tenant dialog, the admin Documents tab) signs the
   * storage path and opens it in a tab, where the browser renders it. This
   * does the same, so "View PDF" means the same thing everywhere.
   *
   * A protocol with no stored file still falls back to the flow: the Done step
   * can regenerate a tenant walkthrough client-side from its photos and
   * inventory, and sending them there beats a dead button.
   */
  const viewPdf = async (protocol: Protocol) => {
    if (!protocol.pdf_storage_path) {
      onStart(protocol.id);
      return;
    }
    if (viewingPdfOf) return;
    // Opened inside the click's gesture — signing is async, and a tab opened
    // after the await is a popup the browser blocks.
    const tab = openPendingDocumentTab();
    setViewingPdfOf(protocol.id);
    try {
      const url = await getSignedUrl(protocol.pdf_storage_path);
      if (!url) throw new Error("could not sign the protocol pdf");
      tab.navigate(url);
    } catch {
      tab.cancel();
      toast.error(t.viewPdfFailed);
    } finally {
      setViewingPdfOf(null);
    }
  };

  const handlePrimary = async () => {
    if (!primary || !booking) return;
    if (primary.resume) {
      onStart(primary.resume.id);
      return;
    }
    try {
      const protocol = await create.mutateAsync({
        bookingId: booking.id,
        kind: primary.kind,
      });
      onStart(protocol.id);
    } catch (err: any) {
      toast.error(err?.message ?? t.errorCreate);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="grid grid-cols-1 gap-0 md:grid-cols-[1.05fr_1fr]">
        {/* Hero */}
        <div className="p-6 md:p-8">
          <div className="mb-1 flex items-center gap-2 text-sm text-neutral-500">
            {t.greeting} {firstName} <span aria-hidden>👋</span>
          </div>
          <h2 className="break-words text-[30px] font-bold leading-[1.05] tracking-tight text-neutral-900 md:text-4xl">
            {t.title}
          </h2>
          <p className="mt-3 text-sm leading-snug text-neutral-500 md:max-w-md">
            {t.subtitle}
          </p>
          <ul className="mt-5 flex flex-col gap-1.5 text-sm text-neutral-600 md:gap-2">
            <Bullet>{t.bulletDuration}</Bullet>
            <Bullet>{t.bulletPause}</Bullet>
            <Bullet>{t.bulletStorage}</Bullet>
          </ul>
        </div>

        {/* Status panel */}
        <div className="border-t border-neutral-100 bg-neutral-50/60 p-6 md:border-l md:border-t-0 md:p-8">
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 md:p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
              {t.yourHome}
            </div>
            {bookingLoading ? (
              <div className="flex items-center py-3 text-neutral-400">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : booking ? (
              <>
                <div className="mt-1 text-[15px] font-semibold text-neutral-900">
                  {booking.property_name ?? t.fallbackHome}
                </div>
                <div className="text-sm text-neutral-500">
                  {booking.room_number
                    ? `${t.room} ${booking.room_number}`
                    : t.room}
                  {booking.address ? ` · ${booking.address}` : ""}
                </div>
              </>
            ) : (
              <div className="mt-1 text-sm text-neutral-500">{t.noBooking}</div>
            )}
          </div>

          <div className="mt-3 space-y-3">
            <KindStatusCard
              icon={<LogIn className="h-5 w-5" />}
              title={t.kindMoveIn}
              t={t}
              state={moveInState}
              language={language}
              kind="move_in"
              actionNeeded={
                actionNeeded?.kind === "move_in" ? actionNeeded : null
              }
              date={booking?.move_in_date ?? null}
              onViewPdf={
                moveIn && moveInState.kindStatus === "submitted"
                  ? () => void viewPdf(moveIn)
                  : undefined
              }
              viewPdfBusy={viewingPdfOf != null && viewingPdfOf === moveIn?.id}
            />
            <KindStatusCard
              icon={<LogOut className="h-5 w-5" />}
              title={t.kindMoveOut}
              t={t}
              state={moveOutState}
              language={language}
              kind="move_out"
              actionNeeded={
                actionNeeded?.kind === "move_out" ? actionNeeded : null
              }
              date={booking?.move_out_date ?? null}
              onViewPdf={
                moveOut && moveOutState.kindStatus === "submitted"
                  ? () => void viewPdf(moveOut)
                  : undefined
              }
              viewPdfBusy={viewingPdfOf != null && viewingPdfOf === moveOut?.id}
            />
          </div>

          <div className="mt-5 flex flex-col-reverse items-stretch gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <p className="text-xs text-neutral-500 md:text-sm">
              {primary
                ? t.pauseHint
                : moveInState.kindStatus === "submitted" &&
                    moveOutState.kindStatus === "submitted"
                  ? t.bothDone
                  : moveOutState.locked
                    ? t.lockedHint
                    : t.pauseHint}
            </p>
            {primary ? (
              <button
                type="button"
                onClick={handlePrimary}
                disabled={!booking || create.isPending || protocolsLoading}
                className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-semibold text-white shadow-md shadow-emerald-500/30 transition active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:shadow-none md:w-auto md:min-w-[200px] ${
                  // Pulsing halo while this button is the action the tab
                  // strip's pip is pointing at (COL-368).
                  actionNeeded && actionNeeded.kind === primary.kind
                    ? "animate-cta-pulse motion-reduce:animate-none"
                    : ""
                }`}
              >
                {create.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {primary.resume
                  ? t.resumeDraft
                  : primary.kind === "move_in"
                    ? t.startMoveIn
                    : t.startMoveOut}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="inline-flex items-center gap-2">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
      {children}
    </li>
  );
}

function KindStatusCard({
  icon,
  title,
  t,
  state,
  language,
  kind,
  onViewPdf,
  viewPdfBusy = false,
  actionNeeded = null,
  date = null,
}: {
  icon: React.ReactNode;
  title: string;
  t: ProtocolCopy;
  state: KindState;
  language: string;
  kind: ProtocolKind;
  /** When provided, renders a "View PDF" button that opens the stored PDF. */
  onViewPdf?: () => void;
  /** True while the storage path is being signed, so the button can wait. */
  viewPdfBusy?: boolean;
  /** Set when this kind is the one the tab strip's pip points at (COL-368). */
  actionNeeded?: ProtocolAction | null;
  /** The move date (YYYY-MM-DD) named in the action-needed hint. */
  date?: string | null;
}) {
  const submittedLabel = state.submittedAt
    ? formatMsg(t.submittedOn, {
        date: formatDate(state.submittedAt, language),
      })
    : null;
  const lockedLabel = state.availableFrom
    ? formatMsg(t.lockedUntil, {
        date: formatDate(state.availableFrom, language),
      })
    : null;

  const tone = (() => {
    if (actionNeeded)
      return {
        border: "border-rose-300",
        iconBg: "bg-rose-100 text-rose-600",
        statusBg: "bg-rose-500 text-white",
      };
    if (state.kindStatus === "submitted")
      return {
        border: "border-emerald-300",
        iconBg: "bg-emerald-100 text-emerald-600",
        statusBg: "bg-emerald-500 text-white",
      };
    if (state.kindStatus === "draft")
      return {
        border: "border-amber-300",
        iconBg: "bg-amber-100 text-amber-600",
        statusBg: "bg-amber-500 text-white",
      };
    if (state.kindStatus === "expired" || state.kindStatus === "closed")
      return {
        border: "border-neutral-200",
        iconBg: "bg-neutral-100 text-neutral-500",
        statusBg: "bg-neutral-400 text-white",
      };
    if (state.locked)
      return {
        border: "border-neutral-200",
        iconBg: "bg-neutral-100 text-neutral-400",
        statusBg: "bg-neutral-200 text-neutral-600",
      };
    return {
      border: "border-neutral-200",
      iconBg: "bg-neutral-100 text-neutral-600",
      statusBg: "bg-neutral-200 text-neutral-700",
    };
  })();

  const statusText = actionNeeded
    ? t.actionNeeded
    : state.kindStatus === "submitted"
      ? t.statusSubmitted
      : state.kindStatus === "draft"
        ? t.statusDraft
        : state.kindStatus === "expired"
          ? t.statusExpired
          : state.kindStatus === "closed"
            ? t.statusClosed
            : state.locked
              ? t.statusLocked
              : t.statusNotStarted;

  // Hint line: prefer submittedOn / lockedUntil; fall back to contextual hint.
  const hint = (() => {
    if (actionNeeded) {
      if (actionNeeded.reason === "upcoming" && actionNeeded.availableFrom)
        return formatMsg(t.actionNeededUpcomingHint, {
          date: formatDate(actionNeeded.availableFrom, language),
        });
      // A draft or an admin reopen can fire without a move date on the
      // booking; don't render a dangling "· move-in on ".
      if (!date)
        return kind === "move_in"
          ? t.actionNeededMoveInHintNoDate
          : t.actionNeededMoveOutHintNoDate;
      return formatMsg(
        kind === "move_in"
          ? t.actionNeededMoveInHint
          : t.actionNeededMoveOutHint,
        { date: formatDate(date, language) }
      );
    }
    if (submittedLabel) return submittedLabel;
    if (lockedLabel) return lockedLabel;
    if (state.locked)
      return kind === "move_in" ? t.moveInLockedHint : t.lockedHint;
    if (state.kindStatus === "closed") return t.closedHint;
    if (state.kindStatus === "expired")
      return kind === "move_in" ? t.moveInExpiredHint : t.moveOutExpiredHint;
    return " ";
  })();

  return (
    <div
      className={`flex w-full items-start gap-3 rounded-2xl border bg-white px-4 py-3.5 ${tone.border}`}
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone.iconBg}`}
      >
        {(state.locked && state.kindStatus === "not_started") ||
        state.kindStatus === "expired" ||
        state.kindStatus === "closed" ? (
          <Lock className="h-5 w-5" />
        ) : (
          icon
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-semibold text-neutral-900">
            {title}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone.statusBg}`}
          >
            {statusText}
          </span>
        </div>
        <div className="text-sm text-neutral-500">{hint}</div>
        {state.ref && state.kindStatus === "submitted" ? (
          <div className="mt-1 text-xs font-mono text-neutral-400">
            {t.refLabelInline} {state.ref}
          </div>
        ) : null}
        {/* "View PDF" affordance for submitted protocols — opens the captured
            PDF after the window has closed (COL-212 / COL-215), or the one
            coliviq staff filed on their behalf. The expired-without-any-record
            case has no PDF at all, so we only render a small "No PDF on file"
            tag there. */}
        {onViewPdf && state.kindStatus === "submitted" ? (
          <button
            type="button"
            onClick={onViewPdf}
            disabled={viewPdfBusy}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-wait"
          >
            {viewPdfBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            {t.viewPdf}
          </button>
        ) : null}
        {state.kindStatus === "expired" || state.kindStatus === "closed" ? (
          <div className="mt-1 text-[11px] uppercase tracking-wider text-neutral-400">
            {t.noPdfAvailable}
          </div>
        ) : null}
      </div>
      {state.kindStatus === "submitted" ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">
          <CheckCircle2 className="h-4 w-4" />
        </span>
      ) : null}
    </div>
  );
}

function formatDate(iso: string, language: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
